import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Button, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDate, localeMap } from '../utils/formatterUtils'; // Import formatDate and localeMap
import config from '../config/config'; // Import configuration

const MilestoneEditPopup = ({ dialogOpen, handleDialogClose, currentMilestone, handleMilestoneChange, tasks, handleTaskChange, handleAddTask, handleSaveMilestone }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isDirty, setIsDirty] = useState(false);
  const [milestone, setMilestone] = useState(currentMilestone || {});

  useEffect(() => {
    setMilestone(currentMilestone || {});
    setIsDirty(false);
  }, [dialogOpen, currentMilestone]);

  const handleFieldChange = (e, taskId, milestoneId) => {
    setIsDirty(true);
    handleTaskChange(e, taskId, milestoneId);
  };

  const handleMilestoneFieldChange = (e) => {
    const { name, value } = e.target;
    setMilestone((prevMilestone) => ({
      ...prevMilestone,
      [name]: value,
    }));
    setIsDirty(true);
  };

  const handleDateChange = (date, name) => {
    setMilestone((prevMilestone) => ({
      ...prevMilestone,
      [name]: date ? date.toISOString().split('T')[0] : '',
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      await handleSaveMilestone(milestone);
      setSnackbarMessage('Save successful!');
      setSnackbarSeverity('success');
      setIsDirty(false);
    } catch (error) {
      setSnackbarMessage('Save failed!');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {milestone.id ? 'Edit Milestone' : 'Add Milestone'}
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {milestone.id ? 'Edit the details of the milestone.' : 'Enter the details of the new milestone.'}
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          name="name"
          value={milestone.name || ''}
          onChange={handleMilestoneFieldChange}
          fullWidth
        />
        <TextField
          margin="dense"
          label="Description"
          name="description"
          value={milestone.description || ''}
          onChange={handleMilestoneFieldChange}
          fullWidth
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label="Start Date"
            value={milestone.startDate ? new Date(milestone.startDate) : null}
            onChange={(date) => handleDateChange(date, 'startDate')}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label="End Date"
            value={milestone.endDate ? new Date(milestone.endDate) : null}
            onChange={(date) => handleDateChange(date, 'endDate')}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label="Due Date"
            value={milestone.dueDate ? new Date(milestone.dueDate) : null}
            onChange={(date) => handleDateChange(date, 'dueDate')}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
        <TextField
          margin="dense"
          label="Status"
          name="status"
          value={milestone.status || ''}
          onChange={handleMilestoneFieldChange}
          fullWidth
        />
        {milestone.id && (
          <>
            <Typography variant="h6" gutterBottom>
              Tasks
            </Typography>
            <Button variant="contained" color="primary" onClick={() => handleAddTask(milestone.id)}>
              Add Task
            </Button>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks[milestone.id] && tasks[milestone.id].map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <TextField
                          name="name"
                          value={task.name}
                          onChange={(e) => handleFieldChange(e, task.id, milestone.id)}
                          fullWidth
                          multiline
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          name="status"
                          value={task.status}
                          onChange={(e) => handleFieldChange(e, task.id, milestone.id)}
                          fullWidth
                          multiline
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          name="assignedTo"
                          value={task.assignedTo}
                          onChange={(e) => handleFieldChange(e, task.id, milestone.id)}
                          fullWidth
                          multiline
                        />
                      </TableCell>
                      <TableCell>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                          <DatePicker
                            label="Start Date"
                            value={task.startDate ? new Date(task.startDate) : null}
                            onChange={(date) => handleFieldChange({ target: { name: 'startDate', value: date ? date.toISOString().split('T')[0] : '' } }, task.id, milestone.id)}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                          />
                        </LocalizationProvider>
                      </TableCell>
                      <TableCell>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                          <DatePicker
                            label="End Date"
                            value={task.endDate ? new Date(task.endDate) : null}
                            onChange={(date) => handleFieldChange({ target: { name: 'endDate', value: date ? date.toISOString().split('T')[0] : '' } }, task.id, milestone.id)}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                          />
                        </LocalizationProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" disabled={!isDirty}>
          Save
        </Button>
      </DialogActions>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default MilestoneEditPopup;